<template>
  <div class="services-list-view">
    <!-- Desktop: Table Layout -->
    <table class="services-table">
      <thead>
        <tr>
          <th>Id</th>
          <th>{{ t('Serviço') }}</th>
          <th>{{ t('Observação') }}</th>
          <th>{{ t('Status') }}</th>
          <th>{{ t('Ação') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="service in services" :key="service.compromisso_servico_id">
          <td class="id-column">
            <a
              v-if="showEditLink"
              href="javascript:void(0)"
              class="edit-link"
              :data-testid="`agenda-servicos-edit-${service.compromisso_servico_id}`"
              @click.prevent="handleEditClick(service.compromisso_servico_id ?? 0)"
            >
              {{ service.compromisso_servico_id }}
              <i class="pi pi-pencil"></i>
            </a>
            <span v-else>#{{ service.compromisso_servico_id }}</span>
          </td>
          <td>
            <span
              :style="{
                'text-decoration': service.compromisso_servico_status === 6 ? 'line-through' : 'none'
              }"
            >
              {{ service.servico_nome }}
            </span>
          </td>
          <td class="obs-column">
            <input-text-area
              :elem-id="`agenda_servicos_obs_${service.compromisso_servico_id}`"
              :data-testid="`agenda-servicos-obs-${service.compromisso_servico_id}`"
              :model-value="(service.compromisso_servico_observacao as string) ?? ''"
              @update:model-value="(v: string) => ((service as Record<string, unknown>).compromisso_servico_observacao = v)"
              :placeholder="t('Observação...')"
              :rows="2"
              :fluid="true"
            />
          </td>
          <td>
            <div
              v-if="!hasPhotos(service)"
              class="status-badge status-pending"
            >
              <i class="pi pi-times"></i> {{ t('Pendente') }}
            </div>
            <div v-else class="status-badge status-ok">
              <i class="pi pi-check"></i> {{ t('Ok') }}
            </div>
          </td>
          <td>
            <servico-photo-upload
              :key="`photo-${service.compromisso_servico_id}-${mode}-${service.tem_imagens_antes}-${service.tem_imagens_depois}`"
              :servico-id="Number(service.compromisso_servico_id ?? 0)"
              :servico-nome="service.servico_nome ?? ''"
              :antes-depois="mode === 'antes' ? 'a' : 'd'"
              source-mode="camera"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Mobile: Card Layout -->
    <div class="services-cards">
      <div
        v-for="service in services"
        :key="service.compromisso_servico_id"
        class="service-card"
      >
        <div class="card-header">
          <div class="card-id">
            <a
              v-if="showEditLink"
              href="javascript:void(0)"
              class="edit-link"
              :data-testid="`agenda-servicos-edit-mobile-${service.compromisso_servico_id}`"
              @click.prevent="handleEditClick(service.compromisso_servico_id ?? 0)"
            >
              <strong>Id:</strong> {{ service.compromisso_servico_id }}
              <i class="pi pi-pencil"></i>
            </a>
            <span v-else><strong>Id:</strong> #{{ service.compromisso_servico_id }}</span>
          </div>
          <div
            v-if="!hasPhotos(service)"
            class="status-badge status-pending"
          >
            <i class="pi pi-times"></i> {{ t('Pendente') }}
          </div>
          <div v-else class="status-badge status-ok">
            <i class="pi pi-check"></i> {{ t('Ok') }}
          </div>
        </div>

        <div class="card-body">
          <div class="card-field">
            <span class="field-label">{{ t('Serviço') }}:</span>
            <span
              class="field-value"
              :style="{
                'text-decoration': service.compromisso_servico_status === 6 ? 'line-through' : 'none'
              }"
            >
              {{ service.servico_nome }}
            </span>
          </div>

          <div class="card-field">
            <span class="field-label">{{ t('Observação') }}:</span>
            <input-text-area
              :elem-id="`agenda_servicos_obs_mobile_${service.compromisso_servico_id}`"
              :data-testid="`agenda-servicos-obs-mobile-${service.compromisso_servico_id}`"
              :model-value="(service.compromisso_servico_observacao as string) ?? ''"
              @update:model-value="(v: string) => ((service as Record<string, unknown>).compromisso_servico_observacao = v)"
              :placeholder="t('Observação...')"
              :rows="2"
              :fluid="true"
            />
          </div>

          <div class="card-field">
            <span class="field-label">{{ t('Ação') }}:</span>
            <div class="field-value">
              <servico-photo-upload
                :key="`photo-${service.compromisso_servico_id}-${mode}-${service.tem_imagens_antes}-${service.tem_imagens_depois}`"
                :servico-id="Number(service.compromisso_servico_id ?? 0)"
                :servico-nome="service.servico_nome ?? ''"
                :antes-depois="mode === 'antes' ? 'a' : 'd'"
                source-mode="camera"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ServicoPhotoUpload from './ServicoPhotoUpload.vue';
import { log } from '../../../utils/DebugLogger';

type Id = number | string;

interface ServicoListItem {
  compromisso_servico_id?: Id;
  servico_nome?: string;
  compromisso_servico_status?: number;
  tem_imagens_antes?: boolean;
  tem_imagens_depois?: boolean;
  [key: string]: unknown;
}

interface Props {
  services: ServicoListItem[];
  mode: 'antes' | 'depois';
  showEditLink?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  services: () => [],
  showEditLink: false
});

const emit = defineEmits<{
  'edit-service': [servicoId: Id];
}>();

const { t } = useI18n();

// Computed
const statusField = computed((): 'tem_imagens_antes' | 'tem_imagens_depois' => {
  return props.mode === 'antes' ? 'tem_imagens_antes' : 'tem_imagens_depois';
});

// Methods
const hasPhotos = (service: ServicoListItem): boolean => {
  return service[statusField.value] === true;
};

const handleEditClick = (servicoId: Id): void => {
  log('ServicesListView', 'handleEditClick', 'Edit link clicked', { servicoId });
  emit('edit-service', servicoId);
};
</script>

<style scoped>
.services-list-view {
  width: 100%;
}

/* Desktop Table Layout */
.services-table {
  width: 100%;
  background-color: white;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.services-table thead th {
  background-color: #f9f9f9;
  font-weight: bold;
  padding: 12px;
  border: 1px solid #ddd;
  text-align: left;
}

.services-table tbody td {
  padding: 12px;
  border: 1px solid #ddd;
  vertical-align: middle;
}

.id-column {
  width: 100px;
  white-space: nowrap;
}

.obs-column {
  width: 180px;
}


.edit-link {
  color: #3b82f6;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.edit-link:hover {
  text-decoration: underline;
}

.edit-link i {
  font-size: 0.875rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.status-pending {
  color: #dd4b39;
}

.status-ok {
  color: #00a65a;
}

/* Mobile Card Layout */
.services-cards {
  display: none;
}

/* Responsive */
@media (max-width: 768px) {
  /* Hide table on mobile */
  .services-table {
    display: none;
  }

  /* Show cards on mobile */
  .services-cards {
    display: block;
  }

  .service-card {
    background: white;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f0f0f0;
  }

  .card-id {
    font-size: 0.95rem;
  }

  .card-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .card-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .field-label {
    font-weight: 600;
    font-size: 0.875rem;
    color: #666;
  }

  .field-value {
    font-size: 0.95rem;
  }
}

@media (max-width: 480px) {
  .service-card {
    padding: 12px;
  }

  .card-id {
    font-size: 0.875rem;
  }

  .field-label {
    font-size: 0.8rem;
  }

  .field-value {
    font-size: 0.875rem;
  }

  .status-badge {
    font-size: 0.875rem;
  }
}
</style>

